// src/pages/InvoiceEdit.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FileText,
  CalendarDays,
  Building2,
  DollarSign,
  Percent,
  Plus,
  Minus,
  Copy as CopyIcon,
  ArrowUp,
  ArrowDown,
  Send,
  Info,
  MapPin,
  ArrowLeft,
} from "lucide-react";

import api from "../api/axios";
import { sendInvoice } from "../services/invoiceService";
// ✅ safe currency utilities
import { formatMoney, safeCurrency } from "../utils/money";

/* ----------------------------- local UI primitives ----------------------------- */
const INPUT_BASE =
  "block w-full h-11 rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] leading-6 text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";
const LABEL_BASE = "text-[13px] font-medium text-slate-700";
const HINT_BASE = "text-[12px] text-slate-500";

function LabeledInput({
  id,
  name,
  label,
  labelRight = null,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  leftAdornment = null,
  rightAdornment = null,
  inputRef,
  onKeyDown,
  onFocus,
  onClick,
  autoComplete = "off",
}) {
  const hasLeft = !!leftAdornment;
  const hasRight = !!rightAdornment;
  return (
    <div className="space-y-1.5">
      {label ? (
        <div className="flex items-center justify-between">
          <label htmlFor={id || name} className={LABEL_BASE}>
            {label} {required ? <span className="text-rose-500">*</span> : null}
          </label>
          {labelRight ? <div className="ml-3 text-[12px]">{labelRight}</div> : null}
        </div>
      ) : null}

      <div className="relative">
        {hasLeft ? (
          <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-slate-400">
            {leftAdornment}
          </div>
        ) : null}
        <input
          ref={inputRef}
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onClick={onClick}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          autoComplete={autoComplete}
          className={[INPUT_BASE, hasLeft ? "pl-10" : "", hasRight ? "pr-10" : ""]
            .filter(Boolean)
            .join(" ")}
          required={required}
        />
        {hasRight ? (
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-slate-400">
            {rightAdornment}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LabeledCheckbox({ label, checked, onChange, disabled = false }) {
  return (
    <label
      className={`inline-flex items-center gap-2 text-[13px] ${
        disabled ? "text-slate-400" : "text-slate-700"
      }`}
    >
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      {label}
    </label>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-2xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-slate-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-slate-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

/* --------------------------------- math (server-aligned) --------------------------------- */
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const clamp = (val, min, max) => Math.min(Math.max(Number(val || 0), min), max);

// 🔧 aligned with InvoiceNew: base subtotal + discount + tax
function calcLine(li) {
  const qty = Math.max(0, Number(li.quantity || 0));
  const rate = Math.max(0, Number(li.rate || 0));
  const taxPct = clamp(li.taxPercent, 0, 100);
  const dPct = clamp(li.discountPercent, 0, 100);
  const dAmt = Math.max(0, Number(li.discountAmount || 0));
  const taxIncl = !!li.isTaxInclusive;

  let unitBase = rate;
  if (taxIncl && taxPct > 0) unitBase = rate / (1 + taxPct / 100);

  const afterDiscount =
    dAmt > 0 ? Math.max(0, unitBase - dAmt) : unitBase * (1 - (dPct > 0 ? dPct / 100 : 0));

  // ✅ base subtotal (before discount)
  const subtotalBase = round2(qty * unitBase);
  // ✅ subtotal after discount (used for tax)
  const subtotal = round2(qty * afterDiscount);
  const lineDiscount = round2(Math.max(0, subtotalBase - subtotal));

  const taxAmount = round2(subtotal * (taxPct > 0 ? taxPct / 100 : 0));
  const total = round2(subtotal + taxAmount);

  return { subtotalBase, subtotal, taxAmount, total, lineDiscount };
}

// ✅ Invoice-level discount REMOVED – totals only use line-level discounts
// ✅ Subtotal is PRE-discount (sum of base amounts)
function computeTotals(lineItems) {
  let subtotalBase = 0;
  let taxTotal = 0;
  let lineDiscountTotal = 0;
  let totalAmount = 0;

  for (const li of lineItems) {
    const c = calcLine(li);
    subtotalBase += c.subtotalBase;
    taxTotal += c.taxAmount;
    lineDiscountTotal += c.lineDiscount || 0;
    totalAmount += c.total;
  }

  subtotalBase = round2(subtotalBase);
  taxTotal = round2(taxTotal);
  lineDiscountTotal = round2(lineDiscountTotal);
  totalAmount = round2(totalAmount);

  return {
    subtotalBase,
    taxTotal,
    totalAmount,
    lineDiscountTotal,
  };
}

/* --------------------------------- helpers --------------------------------- */
const money = (n, c = "USD") => formatMoney(n, safeCurrency(c));
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyAddress = { street: "", city: "", state: "", zip: "", country: "", raw: "" };

// 🔧 FE-normalizer aligned with backend: handles line1/zip/raw/string + legacy shapes
const normalizeAddress = (src) => {
  if (!src) return { ...emptyAddress };
  if (typeof src === "string") {
    return { ...emptyAddress, raw: src };
  }

  const street = src.street || src.line1 || src.addressLine1 || "";
  const city = src.city || "";
  const state = src.state || src.region || src.province || "";
  const zip = src.postalCode || src.zip || "";
  const country = src.country || "";
  const raw = src.raw || src.addressRaw || "";

  return {
    street,
    city,
    state,
    zip,
    country,
    raw,
  };
};

/* --------------------------------- Page --------------------------------- */

export default function InvoiceEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  // basics
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // invoice fields
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSnapshot, setClientSnapshot] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(todayISO());
  const [notes, setNotes] = useState("");

  // line items
  const [lineItems, setLineItems] = useState([]);
  const [defaultTaxPct, setDefaultTaxPct] = useState(0);
  const [defaultTaxInclusive, setDefaultTaxInclusive] = useState(false);

  // addresses
  const [billingAddress, setBillingAddress] = useState({ ...emptyAddress });
  const [shippingAddress, setShippingAddress] = useState({ ...emptyAddress });
  const [useBillingAsShipping, setUseBillingAsShipping] = useState(false);

  // anchor (parity)
  const inputAnchorRef = useRef(null);

  const invalidDueDate =
    issueDate && dueDate ? new Date(dueDate) < new Date(issueDate) : false;

  const canSave =
    clientId &&
    lineItems.length > 0 &&
    lineItems.some(
      (li) =>
        Number(li.quantity) > 0 &&
        (Number(li.rate) > 0 || Number(li.discountAmount) > 0)
    ) &&
    !invalidDueDate;

  const totals = useMemo(() => computeTotals(lineItems), [lineItems]);

  // 🔢 aggregated discount: line-level only
  const aggregatedDiscountDisplay = totals.lineDiscountTotal || 0;

  /* ---------------------------- load existing invoice ---------------------------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoices/${id}`);
      const inv = res?.data?.data || res?.data;

      if (!inv) throw new Error("Invoice not found");

      setInvoiceNumber(inv.invoiceNumber || "");
      setClientId(inv.clientId || inv.client?._id || "");
      setClientSnapshot(inv.clientSnapshot || null);
      setCurrency((inv.currency || "USD").toUpperCase());
      setIssueDate(inv.issueDate ? inv.issueDate.slice(0, 10) : todayISO());
      setDueDate(
        inv.dueDate
          ? inv.dueDate.slice(0, 10)
          : inv.issueDate
          ? inv.issueDate.slice(0, 10)
          : todayISO()
      );
      setNotes(inv.notes || "");

      // 🔧 addresses – smarter fallback:
      // 1) non-empty invoice.billingAddress
      // 2) clientSnapshot.address
      // 3) inv.client.address (if populated)
      let rawBill = inv.billingAddress;
      if (typeof rawBill === "string") {
        rawBill = { raw: rawBill };
      }

      const hasInvoiceBilling =
        rawBill &&
        typeof rawBill === "object" &&
        Object.keys(rawBill).length > 0 &&
        Object.values(rawBill).some((v) =>
          typeof v === "string" ? v.trim() !== "" : v != null
        );

      if (!hasInvoiceBilling) {
        rawBill = inv.clientSnapshot?.address || inv.client?.address || {};
      }

      let rawShipFromInvoice = inv.shippingAddress;
      if (typeof rawShipFromInvoice === "string") {
        rawShipFromInvoice = { raw: rawShipFromInvoice };
      }

      const hasShip =
        rawShipFromInvoice &&
        typeof rawShipFromInvoice === "object" &&
        Object.values(rawShipFromInvoice).some((v) =>
          typeof v === "string" ? v.trim() !== "" : v != null
        );

      const bill = normalizeAddress(rawBill);
      const ship = normalizeAddress(hasShip ? rawShipFromInvoice : rawBill);

      setBillingAddress(bill);
      setShippingAddress(ship);

      setUseBillingAsShipping(
        !hasShip ||
          ["street", "city", "state", "zip", "country", "raw"].every(
            (k) => (bill?.[k] || "") === (ship?.[k] || "")
          )
      );

      // line items — map both `rate` and legacy `unitPrice` safely
      const lis = Array.isArray(inv.lineItems) ? inv.lineItems : [];
      const mapped = lis.map((li) => ({
        _id: li._id, // keep if backend uses it
        description: li.description || li.name || "",
        quantity: Number(li.quantity || 0),
        rate: Number(li.rate ?? li.unitPrice ?? 0),
        taxPercent: Number(li.taxPercent || 0),
        discountPercent: Number(li.discountPercent || 0),
        discountAmount: Number(li.discountAmount || 0),
        isTaxInclusive: !!li.isTaxInclusive,
        showDiscount:
          Number(li.discountAmount) > 0 || Number(li.discountPercent) > 0,
      }));
      setLineItems(mapped);

      // sensible defaults from first line
      if (mapped.length) {
        setDefaultTaxPct(Number(mapped[0].taxPercent || 0));
        setDefaultTaxInclusive(!!mapped[0].isTaxInclusive);
      } else {
        setDefaultTaxPct(0);
        setDefaultTaxInclusive(false);
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load invoice"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  /* ------------------------------- handlers ------------------------------- */
  const patchLine = (i, field, val) =>
    setLineItems((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      return next;
    });

  const addLine = () =>
    setLineItems((prev) => [
      ...prev,
      {
        description: "",
        quantity: 1,
        rate: 0,
        taxPercent: defaultTaxPct,
        discountPercent: 0,
        discountAmount: 0,
        isTaxInclusive: defaultTaxInclusive,
        showDiscount: false,
      },
    ]);

  const removeLine = (i) =>
    setLineItems((prev) => prev.filter((_, idx) => idx !== i));

  const duplicateLine = (i) =>
    setLineItems((prev) => {
      const copy = { ...prev[i] };
      // remove persisted _id if present
      delete copy._id;
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });

  const moveLine = (i, dir) =>
    setLineItems((prev) => {
      const next = [...prev];
      const ni = i + dir;
      if (ni < 0 || ni >= next.length) return prev;
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    });

  const applyDefaultsToAll = () => {
    setLineItems((prev) =>
      prev.map((li) => ({
        ...li,
        taxPercent: defaultTaxPct,
        isTaxInclusive: defaultTaxInclusive,
      }))
    );
    toast.success("Applied defaults to all lines");
  };

  const onBillingChange = (name, value) => {
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
  };
  const onShippingChange = (name, value) => {
    setShippingAddress((prev) => ({ ...prev, [name]: value }));
  };

  const save = async ({ closeAfter = false } = {}) => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        clientId,
        issueDate: issueDate || undefined,
        dueDate: dueDate || undefined,
        currency: (currency || "USD").toUpperCase(),
        notes,
        // IMPORTANT: send both rate and unitPrice for compatibility
        lineItems: lineItems.map((li) => ({
          _id: li._id, // keep if backend supports it; harmless if ignored
          description: li.description || "",
          quantity: Number(li.quantity || 0),
          rate: Number(li.rate || 0),
          unitPrice: Number(li.rate || 0),
          taxPercent: clamp(li.taxPercent, 0, 100),
          discountPercent: clamp(li.discountPercent, 0, 100),
          discountAmount: Number(li.discountAmount || 0),
          isTaxInclusive: !!li.isTaxInclusive,
        })),
        billingAddress,
        shippingAddress: useBillingAsShipping
          ? { ...billingAddress }
          : shippingAddress,
        // ❌ invoice-level discount removed
        // ❌ baseCurrency / exchangeRate removed (single-currency here)
      };

      await api.put(`/invoices/${id}`, payload);
      toast.success("Invoice updated");

      if (closeAfter) navigate(`/invoices/${id}`);
      else load();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to update invoice";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onSend = async () => {
    try {
      await sendInvoice(id);
      toast.success("Invoice sent");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send");
    }
  };

  /* ------------------------------ UI ------------------------------ */
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-slate-200 shadow-sm h-40 bg-white animate-pulse" />
        <div className="h-6" />
        <div className="rounded-2xl border border-slate-200 shadow-sm h-[60vh] bg-white animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* normalize native date widget differences */}
      <style>{`
        input[type="date"]::-webkit-inner-spin-button{display:none;}
        input[type="date"]::-webkit-clear-button{display:none;}
        input[type="date"]::-webkit-calendar-picker-indicator{opacity:0;}
      `}</style>

      {/* Header – aligned with QuoteEdit (no gradient) */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-slate-200 shadow-sm bg-white">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="inline -mt-1 mr-1" size={16} /> Back
                </button>
                <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center">
                  <FileText size={20} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Edit Invoice {invoiceNumber ? `• ${invoiceNumber}` : ""}
                  </h1>
                  <p className="text-slate-600 text-sm">
                    Update details and line items, then save.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => save({ closeAfter: false })}
                  disabled={!canSave || saving}
                  className={`px-4 py-2 rounded-xl font-semibold shadow-sm border border-slate-200 text-slate-700 hover:bg-slate-50 ${
                    !canSave || saving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => save({ closeAfter: true })}
                  disabled={!canSave || saving}
                  className={`px-4 py-2 rounded-xl font-semibold shadow-sm bg-slate-900 text-white hover:bg-black/90 ${
                    !canSave || saving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  Save & Close
                </button>
                <button
                  type="button"
                  onClick={onSend}
                  className="px-4 py-2 rounded-xl font-semibold shadow-sm border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <Send size={16} className="inline -mt-0.5 mr-2" />
                  Send
                </button>
              </div>
            </div>

            {/* Quick Stats – aligned with InvoiceNew (pre-discount subtotal) */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs flex items-center gap-1">
                  <DollarSign size={14} /> Subtotal
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.subtotalBase, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs flex items-center gap-1">
                  <Percent size={14} /> Tax
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.taxTotal, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs">Line discounts</div>
                <div className="text-lg font-bold text-slate-900">
                  -{money(aggregatedDiscountDisplay, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs">Total</div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.totalAmount, currency)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Currency (client is fixed here to avoid accidental reassignment) */}
          <Section
            icon={Building2}
            title="Client & Currency"
            hint="Client from original invoice; currency editable"
          >
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-4 items-start">
              <div>
                <label className={LABEL_BASE}>Client</label>
                <div className="mt-1 h-11 px-3.5 flex items-center rounded-xl border bg-slate-50 border-slate-200 text-[0.95rem] text-slate-700">
                  {clientSnapshot?.name || "—"}
                  {clientSnapshot?.email ? (
                    <span className="ml-2 text-slate-500 text-[12px]">
                      • {clientSnapshot.email}
                    </span>
                  ) : null}
                </div>
              </div>
              <LabeledInput
                name="currency"
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                placeholder="USD"
              />
            </div>
          </Section>

          {/* Dates */}
          <Section
            icon={CalendarDays}
            title="Dates & Terms"
            hint="Change issue and due dates"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <LabeledInput
                name="issueDate"
                label="Issue Date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                type="date"
                rightAdornment={<CalendarDays size={16} />}
                onClick={(e) => e.target.showPicker?.()}
              />
              <LabeledInput
                name="dueDate"
                label="Due Date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                type="date"
                rightAdornment={<CalendarDays size={16} />}
                onClick={(e) => e.target.showPicker?.()}
              />
            </div>
            {invalidDueDate ? (
              <div className="text-[12px] text-rose-600 mt-1">
                Due date must be on or after Issue date.
              </div>
            ) : null}
          </Section>

          {/* Addresses */}
          <Section
            icon={MapPin}
            title="Addresses"
            hint="Edit billing/shipping as needed"
          >
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <LabeledCheckbox
                label="Shipping same as billing"
                checked={useBillingAsShipping}
                onChange={(e) => setUseBillingAsShipping(e.target.checked)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Billing */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-700">
                  Billing Address
                </div>
                <LabeledInput
                  label="Street"
                  value={billingAddress.street}
                  onChange={(e) =>
                    onBillingChange("street", e.target.value)
                  }
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabeledInput
                    label="City"
                    value={billingAddress.city}
                    onChange={(e) =>
                      onBillingChange("city", e.target.value)
                    }
                  />
                  <LabeledInput
                    label="State/Province"
                    value={billingAddress.state}
                    onChange={(e) =>
                      onBillingChange("state", e.target.value)
                    }
                  />
                  <LabeledInput
                    label="ZIP/Postal"
                    value={billingAddress.zip}
                    onChange={(e) =>
                      onBillingChange("zip", e.target.value)
                    }
                  />
                </div>
                <LabeledInput
                  label="Country"
                  value={billingAddress.country}
                  onChange={(e) =>
                    onBillingChange("country", e.target.value)
                  }
                />
                <label className={LABEL_BASE}>Raw (optional)</label>
                <textarea
                  rows={3}
                  className={INPUT_BASE + " h-auto"}
                  value={billingAddress.raw}
                  onChange={(e) =>
                    onBillingChange("raw", e.target.value)
                  }
                  placeholder="Street, City, State, ZIP, Country"
                />
              </div>

              {/* Shipping */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-700">
                  Shipping Address
                </div>
                <LabeledInput
                  label="Street"
                  value={
                    useBillingAsShipping
                      ? billingAddress.street
                      : shippingAddress.street
                  }
                  onChange={(e) =>
                    onShippingChange("street", e.target.value)
                  }
                  disabled={useBillingAsShipping}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabeledInput
                    label="City"
                    value={
                      useBillingAsShipping
                        ? billingAddress.city
                        : shippingAddress.city
                    }
                    onChange={(e) =>
                      onShippingChange("city", e.target.value)
                    }
                    disabled={useBillingAsShipping}
                  />
                  <LabeledInput
                    label="State/Province"
                    value={
                      useBillingAsShipping
                        ? billingAddress.state
                        : shippingAddress.state
                    }
                    onChange={(e) =>
                      onShippingChange("state", e.target.value)
                    }
                    disabled={useBillingAsShipping}
                  />
                  <LabeledInput
                    label="ZIP/Postal"
                    value={
                      useBillingAsShipping
                        ? billingAddress.zip
                        : shippingAddress.zip
                    }
                    onChange={(e) =>
                      onShippingChange("zip", e.target.value)
                    }
                    disabled={useBillingAsShipping}
                  />
                </div>
                <LabeledInput
                  label="Country"
                  value={
                    useBillingAsShipping
                      ? billingAddress.country
                      : shippingAddress.country
                  }
                  onChange={(e) =>
                    onShippingChange("country", e.target.value)
                  }
                  disabled={useBillingAsShipping}
                />
                <label className={LABEL_BASE}>Raw (optional)</label>
                <textarea
                  rows={3}
                  className={INPUT_BASE + " h-auto"}
                  value={
                    useBillingAsShipping
                      ? billingAddress.raw
                      : shippingAddress.raw
                  }
                  onChange={(e) =>
                    onShippingChange("raw", e.target.value)
                  }
                  placeholder="Street, City, State, ZIP, Country"
                  disabled={useBillingAsShipping}
                />
              </div>
            </div>
          </Section>

          {/* Line Items */}
          <Section
            icon={DollarSign}
            title="Items & Services"
            hint="Discount amount overrides percent; 'Tax inclusive' backs out tax from rate."
          >
            {/* Defaults Toolbar */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-28">
                <LabeledInput
                  name="defaultTax"
                  label="Default Tax %"
                  type="number"
                  value={defaultTaxPct}
                  onChange={(e) =>
                    setDefaultTaxPct(Number(e.target.value))
                  }
                />
              </div>
              <div className="pt-6">
                <LabeledCheckbox
                  label="Tax inclusive"
                  checked={defaultTaxInclusive}
                  onChange={(e) =>
                    setDefaultTaxInclusive(e.target.checked)
                  }
                />
              </div>
              <button
                type="button"
                className="ml-auto px-3.5 py-2.5 rounded-xl border text-sm hover:bg-slate-50"
                onClick={applyDefaultsToAll}
              >
                Apply defaults to all
              </button>
            </div>

            {/* Cards */}
            <div className="space-y-4 mt-3">
              {lineItems.map((li, i) => {
                const c = calcLine(li);
                const hasDiscount =
                  Number(li.discountAmount) > 0 ||
                  Number(li.discountPercent) > 0;

                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-slate-700">
                        Item {i + 1}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-slate-50"
                          title="Move up"
                          onClick={() => moveLine(i, -1)}
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-slate-50"
                          title="Move down"
                          onClick={() => moveLine(i, 1)}
                        >
                          <ArrowDown size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-slate-50"
                          title="Duplicate"
                          onClick={() => duplicateLine(i)}
                        >
                          <CopyIcon size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-rose-50 text-rose-600"
                          title="Remove"
                          onClick={() => removeLine(i)}
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <LabeledInput
                        label="Qty"
                        type="number"
                        value={li.quantity}
                        onChange={(e) =>
                          patchLine(i, "quantity", Number(e.target.value))
                        }
                      />
                      <LabeledInput
                        label="Rate"
                        type="number"
                        value={li.rate}
                        onChange={(e) =>
                          patchLine(i, "rate", Number(e.target.value))
                        }
                        rightAdornment={<DollarSign size={14} />}
                      />

                      {/* Discount block */}
                      <div className="col-span-2">
                        {li.showDiscount ? (
                          <div className="grid grid-cols-2 gap-3">
                            <LabeledInput
                              label="Disc %"
                              type="number"
                              value={li.discountPercent}
                              onChange={(e) =>
                                patchLine(
                                  i,
                                  "discountPercent",
                                  Number(e.target.value)
                                )
                              }
                              rightAdornment={<Percent size={14} />}
                            />
                            <LabeledInput
                              label="Disc $"
                              type="number"
                              value={li.discountAmount}
                              onChange={(e) =>
                                patchLine(
                                  i,
                                  "discountAmount",
                                  Number(e.target.value)
                                )
                              }
                              rightAdornment={<DollarSign size={14} />}
                            />
                            <div className="col-span-2">
                              <button
                                type="button"
                                className="text-[12px] underline text-slate-600 hover:text-slate-800"
                                onClick={() =>
                                  patchLine(i, "showDiscount", false)
                                }
                              >
                                Hide discount
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 h-11">
                            <button
                              type="button"
                              className="px-3.5 py-2 rounded-lg border text-sm hover:bg-slate-50"
                              onClick={() =>
                                patchLine(i, "showDiscount", true)
                              }
                            >
                              Add discount
                            </button>
                            {hasDiscount && (
                              <span
                                className="text-[12px] px-2 py-1 rounded-full border bg-slate-50 text-slate-700"
                                title="Click to edit"
                                onClick={() =>
                                  patchLine(i, "showDiscount", true)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                −
                                {Number(li.discountAmount) > 0
                                  ? money(li.discountAmount, currency)
                                  : `${li.discountPercent}%`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <LabeledInput
                        label="Tax %"
                        type="number"
                        value={li.taxPercent}
                        onChange={(e) =>
                          patchLine(
                            i,
                            "taxPercent",
                            Number(e.target.value)
                          )
                        }
                        rightAdornment={<Percent size={14} />}
                      />
                      <div className="pt-7">
                        <LabeledCheckbox
                          label="Tax incl."
                          checked={!!li.isTaxInclusive}
                          onChange={(e) =>
                            patchLine(
                              i,
                              "isTaxInclusive",
                              e.target.checked
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* Description + totals */}
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
                      <div className="lg:col-span-6">
                        <label className={LABEL_BASE}>Description</label>
                        <textarea
                          rows={2}
                          className={INPUT_BASE + " h-auto"}
                          placeholder="Describe the product or service"
                          value={li.description}
                          onChange={(e) =>
                            patchLine(i, "description", e.target.value)
                          }
                        />
                      </div>

                      <div className="lg:col-span-6 grid grid-cols-3 md:grid-cols-6 gap-3 text-[13px]">
                        <div className="col-span-1">
                          <div className="text-slate-500">Subtotal</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.subtotalBase, currency)}
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="text-slate-500">Tax</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.taxAmount, currency)}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-2">
                          <div className="text-slate-500">Line Total</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.total, currency)}
                          </div>
                        </div>
                        <div className="col-span-3 md:col-span-2 flex items-end justify-end">
                          <div className={HINT_BASE}>
                            Amount first, then % (if both set, amount wins).
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center pt-1">
              <div className={HINT_BASE + " flex items-center gap-1"}>
                <Info size={14} />
                Use ↑/↓ to re-order. Totals update instantly.
              </div>
              <button
                type="button"
                className="px-3.5 py-2.5 rounded-xl border text-sm hover:bg-slate-50"
                onClick={addLine}
              >
                <Plus className="inline -mt-1 mr-1" size={16} />
                Add another line
              </button>
            </div>
          </Section>

          {/* Notes */}
          <Section
            icon={FileText}
            title="Notes"
            hint="Shown on the invoice PDF & emails"
          >
            <textarea
              rows={3}
              className={INPUT_BASE + " h-auto"}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional message / terms / thank you note"
            />
          </Section>

          {/* 🔻 Advanced section removed (no invoice-level discount, no multi-currency) */}
        </div>

        {/* Right column: sticky summary */}
        <div className="space-y-6">
          <div className="sticky top-6">
            <Section
              icon={DollarSign}
              title="Summary"
              hint="Review totals before saving"
            >
              <div className="space-y-2 text-[15px]">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-semibold tabular-nums">
                    {money(totals.subtotalBase, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Line discounts</span>
                  <span className="font-semibold tabular-nums">
                    -{money(aggregatedDiscountDisplay, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tax</span>
                  <span className="font-semibold tabular-nums">
                    {money(totals.taxTotal, currency)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-2" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-slate-800">
                    Total
                  </span>
                  <span className="font-extrabold tabular-nums">
                    {money(totals.totalAmount, currency)}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={() => save({ closeAfter: false })}
                  className={`px-4 py-2 rounded-xl font-semibold text-white shadow-sm ${
                    !canSave || saving
                      ? "bg-slate-400"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={() => save({ closeAfter: true })}
                  className={`px-4 py-2 rounded-xl font-semibold border ${
                    !canSave || saving
                      ? "text-slate-400 border-slate-200"
                      : "text-blue-700 border-blue-200 hover:bg-blue-50"
                  }`}
                >
                  Save & Close
                </button>

                <div className="text-[12px] text-slate-500 flex items-center gap-2 mt-2">
                  <Info size={14} className="text-slate-400" />
                  Rounding: HALF_UP • Due date must be ≥ issue date
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
